import { AuthConfig } from 'angular-oauth2-oidc';

import { isDevMode } from '@angular/core';

import { EnvironmentEnum } from '@ng-gems/api';

import { FeatureFlag } from './types/feature-flag';

// these environments are controlled by the docker in the pipeline configuration
// export type RemoteEnvironment = 'dvp' | 'rec2' | 'rec3' | 'ppd' | 'prd';
// export type LocalEnvironment = 'local' | 'test' | 'midback-local' | 'contract-local';
// export type AllEnvironment = RemoteEnvironment & LocalEnvironment;

export enum AppEnvironment {
  'local' = 'local',
  'contract-local' = 'contract-local',
  'midback-local' = 'midback-local',
  'dvp' = 'dvp',
  'test' = 'test',
  'rec2' = 'rec2',
  'rec3' = 'rec3',
  'ppd' = 'ppd',
  'prd' = 'prd',
}

export interface IAppEnv {
  /**
   * control some features
   */
  featureFlags: Partial<Record<FeatureFlag, boolean>>;

  /**
   * loggers
   */
  loggers: string[];

  /**
   * Date of when the terms of use were last updated
   */
  termsOfUseLastUpdated: Date;

  /**
   * host name
   */
  baseRedirectUri: string;

  /**
   * Okta issuer
   */
  oktaIssuer: string;

  /**
   * Okta Client Id
   */
  oktaClientId: string;
}

const termsOfUseLastUpdated = new Date('2025-02-26');

const AppEnv: Record<AppEnvironment, IAppEnv> = {
  [AppEnvironment.local]: {
    baseRedirectUri: 'http://localhost:4200',
    oktaIssuer: 'https://gem-beta.oktapreview.com/oauth2/aus3v6rg3e9zNHMxe0x6',
    oktaClientId: '0oa40eoxbWREDYSG90x6',
    featureFlags: { graphV2: true, useQHNomination: true, useSignalR: true },
    loggers: ['console'],
    termsOfUseLastUpdated,
  },
  [AppEnvironment['contract-local']]: {
    baseRedirectUri: 'http://localhost:4200',
    oktaIssuer: 'https://gem-beta.oktapreview.com/oauth2/aus3v6rg3e9zNHMxe0x6',
    oktaClientId: '0oa40eoxbWREDYSG90x6',
    featureFlags: { graphV2: true, useQHNomination: true, useSignalR: true },
    loggers: ['console'],
    termsOfUseLastUpdated,
  },
  [AppEnvironment['midback-local']]: {
    baseRedirectUri: 'http://localhost:4200',
    oktaIssuer: 'https://gem-beta.oktapreview.com/oauth2/aus3v6rg3e9zNHMxe0x6',
    oktaClientId: '0oa40eoxbWREDYSG90x6',
    featureFlags: { graphV2: true, useQHNomination: true, useSignalR: true },
    loggers: ['console'],
    termsOfUseLastUpdated,
  },
  [AppEnvironment.dvp]: {
    baseRedirectUri: 'https://wattson-dev.gem.engie.com',
    oktaIssuer: 'https://gem-beta.oktapreview.com/oauth2/aus3v6rg3e9zNHMxe0x6',
    oktaClientId: '0oa40eoxbWREDYSG90x6',
    featureFlags: { graphV2: true, useQHNomination: true, useSignalR: true },
    loggers: ['console'],
    termsOfUseLastUpdated,
  },
  [AppEnvironment.test]: {
    baseRedirectUri: 'http://localhost:9876',
    oktaIssuer: 'https://gem-beta.oktapreview.com/oauth2/aus3v6rg3e9zNHMxe0x6',
    oktaClientId: '0oa40eoxbWREDYSG90x6',
    featureFlags: { graphV2: true, useQHNomination: true, useSignalR: true },
    loggers: ['console'],
    termsOfUseLastUpdated,
  },
  [AppEnvironment.rec2]: {
    baseRedirectUri: 'https://wattson-rec.gem.engie.com',
    oktaIssuer: 'https://gem-beta.oktapreview.com/oauth2/aus3v6rg3e9zNHMxe0x6',
    oktaClientId: '0oa40eoxbWREDYSG90x6',
    featureFlags: { graphV2: true, useQHNomination: true, useSignalR: false },
    loggers: [],
    termsOfUseLastUpdated,
  },
  [AppEnvironment.rec3]: {
    baseRedirectUri: 'https://wattson-rec2.gem.engie.com',
    oktaIssuer: 'https://gem-beta.oktapreview.com/oauth2/aus3v6rg3e9zNHMxe0x6',
    oktaClientId: '0oa40eoxbWREDYSG90x6',
    featureFlags: { graphV2: true, useQHNomination: true, useSignalR: false },
    loggers: [],
    termsOfUseLastUpdated,
  },
  [AppEnvironment.ppd]: {
    baseRedirectUri: 'https://wattson-ppd.gem.engie.com',
    oktaIssuer: 'https://gem-beta.oktapreview.com/oauth2/aus3v6rg3e9zNHMxe0x6',
    oktaClientId: '0oa40eoxbWREDYSG90x6',
    featureFlags: { graphV2: false, useQHNomination: true, useSignalR: false },
    loggers: [],
    termsOfUseLastUpdated,
  },
  [AppEnvironment.prd]: {
    baseRedirectUri: 'https://wattson.engie.com',
    oktaIssuer: 'https://gem.okta-emea.com/oauth2/aus2jp8vmz3yYrDnc0i7',
    oktaClientId: '3-b-wattson-EWH9CLDWRB36A5H',
    featureFlags: { graphV2: false, useQHNomination: false, useSignalR: false },
    loggers: [],
    termsOfUseLastUpdated,
  },
};

const OKTA_SCOPES = [
  'openid',
  'profile',
  'email',
  'api.wattson',
  'api.wattson.mid.back.admin',
  'api.salesblotter.orchestrator',
  'api.wattson.woot-feeder.read',
  'api.wattson.supplydata.metering.power.read',
  'api.wattson.supplydata.metering.gas.read',
  'api.footprint-carbon-accounting',
  'api.wattson.supplydata.masterdata.read',
  'api.wattson.supplydata.masterdata.write',
];

export class AppInfo {
  // CONFIG CONTROLLED BY DOCKER
  /**
   * Activate maintenance Mode
   */
  maintenance: boolean;
  // Okta email of technical users, that may have more access,
  // for example they can see atomic roles in order to affect such roles
  // Controlled by ENV docker
  technicalUsers: string[];
  /**
   * The current environment of the application.
   * @type {AppEnvironment}
   */
  environment: AppEnvironment;
  /**
   * The version of the application.
   * @type {string}
   */
  version: string;
  /**
   * The commit hash of the current build.
   * @type {string}
   */
  commit: string;

  constructor(attrs: Partial<AppInfo>) {
    Object.assign(this, attrs);
  }

  get loadedEnvironment(): IAppEnv {
    return AppEnv[this.environment];
  }

  /**
   * Maps the current application environment to the corresponding API environment.
   * @returns {EnvironmentEnum} The mapped API environment.
   */
  get apiEnvironment(): EnvironmentEnum {
    // if (this.environment === 'rec3') return EnvironmentEnum.rec6;
    return EnvironmentEnum[this.environment];
  }

  get environmentLabel(): string {
    return this.environment.startsWith('rec') ? 'REC' : this.environment;
  }

  get isProduction() {
    return this.environment === AppEnvironment.prd;
  }

  isFeatureOff(featureName: FeatureFlag): boolean {
    return !this.isFeatureOn(featureName);
  }

  isFeatureOn(featureName: FeatureFlag): boolean {
    // Read the value from the config service
    if (Object.hasOwn(this.loadedEnvironment.featureFlags, featureName)) {
      return this.loadedEnvironment.featureFlags[featureName];
    }
    return undefined; // if feature not found, default to turned off
  }

  isAllowedEnvironment(envs: AppEnvironment[]) {
    return envs.includes(this.environment);
  }

  isValidEnvironment(): boolean {
    if (isDevMode()) return true;
    return window.location.origin === this.loadedEnvironment.baseRedirectUri;
  }

  get oktaConfig(): AuthConfig {
    const baseUri = !isDevMode() ? this.loadedEnvironment.baseRedirectUri : window.location.origin;
    return {
      issuer: this.loadedEnvironment.oktaIssuer,
      clientId: this.loadedEnvironment.oktaClientId,
      redirectUri: `${baseUri}/callback`,
      postLogoutRedirectUri: `${baseUri}/login`,
      silentRefreshRedirectUri: `${baseUri}/silent-refresh.html`,
      scope: OKTA_SCOPES.join(' '),
    };
  }
}
